import type {
  EditorState,
  TrackType,
} from "../../vendor/src/features/remotion-editor/editor/state/types";
import { getConstrainedTimelineMoveDelta } from "../../vendor/src/features/remotion-editor/editor/keyboard-shortcuts/arrow-key-timeline-move";
import { editableTree, itemOwner } from "./group-split";
import { hasPendingTimelineEdit } from "./edit-ownership";
import { placeTimelineSelection } from "./timeline-place";

/** Fine movement cannot create a reorder splice. It uses the same source/linked-tree translation as numeric placement. */
export function nudgeTimelineSelection(
  state: EditorState,
  ids: readonly string[],
  delta: number,
): EditorState {
  if (!Number.isSafeInteger(delta) || !delta || hasPendingTimelineEdit(state))
    return state;
  const selected = [...new Set(ids)].filter((id) => {
    const own = itemOwner(state, id);
    return own && !own.track.locked && editableTree(state, id);
  });
  const roots = selected.filter((id) => {
    let own = itemOwner(state, id);
    while (own?.parentId) {
      if (selected.includes(own.parentId)) return false;
      own = itemOwner(state, own.parentId);
    }
    return true;
  });
  if (!roots.length) return state;
  const containers = new Map<string, TrackType>();
  for (const id of roots) {
    const own = itemOwner(state, id)!;
    const group = own.parentId ? state.undoableState.items[own.parentId] : null;
    containers.set(
      own.parentId ?? own.track.id,
      group?.type === "group"
        ? { ...own.track, items: group.children }
        : own.track,
    );
  }
  const step = getConstrainedTimelineMoveDelta({
    itemIds: roots,
    requestedDelta: delta,
    items: state.undoableState.items,
    tracks: [...containers.values()],
  });
  if (!step) return state;
  const primary =
    roots.find((id) => {
      const o = itemOwner(state, id)!;
      return (
        !o.parentId &&
        o.track.metadata?.isMainTrack &&
        o.track.metadata.type === "video"
      );
    }) ?? roots[0]!;
  const next = placeTimelineSelection(
    state,
    roots,
    primary,
    state.undoableState.items[primary]!.from + step,
    { intent: "nudge" },
  );
  return next.ok ? { ...next.state, timelineTimeEditApplied: true } : state;
}
